import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  FaUser,
  FaCrosshairs,
  FaMapMarkerAlt,
  FaPencilAlt,
  FaCompass,
  FaCamera,
  FaCheck,
  FaBullseye,
  FaAward,
  FaSave,
} from 'react-icons/fa';
import api from '../../../utils/api';
import { fetchProfile, getCachedProfile, patchCachedProfile } from '../../pro/profileCache';
import { fetchClubs, getCachedClubs, setCachedClubs } from '../../clubs/utils/clubsCache';
import { getCachedBuddiesHub } from '../../../utils/buddiesCache';
import { getCachedHunts, setCachedHunts } from '../../../utils/statsCache';
import {
  fetchHuntLocations,
  getCachedHuntLocations,
} from '../utils/accountHubCache';
import { huntService } from '../../features/services/huntService';
import { AssetPickerModal } from '../../../components/common/AssetPickerModal';
import { assetsService } from '../../../services/assetsService';
import { useToast } from '../../../components/ui/ToastProvider';
import { YButton } from '../../../ui/YButton';
import { YFormField } from '../../../ui/YFormField';
import { YInput } from '../../../ui/YInput';
import { YTextarea } from '../../../ui/YTextarea';
import buddiesService from '../../features/services/buddiesService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { getAvatarInitials } from '../../../components/layout/appChromeMeta';
import { CachedImage } from '../../../components/common/CachedImage';
import { formatLastHuntLine, pickMostRecentHunt } from '../utils/lastHuntDisplay';
import { ProfilePanel, ProfileTip, ProfileStatChip } from './ProfilePanel';
import { AppearancePanel } from './AppearancePanel';
import '../profile-design.css';

const STATUS_PRESETS = [
  'Heading to the stand',
  'In the field',
  'Scouting',
  'At Camp',
  'In the woods',
  'Tracking',
  'On the tractor',
  'Putting out cameras',
];

const WEAPON_OPTIONS = [
  { id: 'rifle', label: 'Rifle' },
  { id: 'bow', label: 'Bow' },
  { id: 'muzzleloader', label: 'Muzzleloader' },
  { id: 'shotgun', label: 'Shotgun' },
  { id: 'crossbow', label: 'Crossbow' },
  { id: 'other', label: 'Other' },
];

const METHOD_LABELS = Object.fromEntries(WEAPON_OPTIONS.map((w) => [w.id, w.label]));

const BIO_MAX = 400;
const STATUS_MAX = 140;

function applyCachedProfileFields(data) {
  const row = data || {};
  return {
    photoUrl: row.photo_url || '',
    firstName: row.first_name || '',
    lastName: row.last_name || '',
    handle: row.handle || '',
    preferredMethod: row.preferred_hunting_method || 'rifle',
    statusText: row.status || '',
    bio: (row.bio || '').slice(0, BIO_MAX),
    primaryLocationId: row.primary_hunt_location_id ? String(row.primary_hunt_location_id) : '',
  };
}

function clubsFromResponse(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.results)) return res.data.results;
  return [];
}

export const ProfileSection = forwardRef(function ProfileSection({ onSaveStateChange }, ref) {
  const { currentUser } = useCurrentUser();
  const toastCtx = useToast();
  const showToast = toastCtx && typeof toastCtx.show === 'function' ? toastCtx.show : () => {};
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const cachedProfile = getCachedProfile({ allowStale: true });
  const cachedProfileFields = applyCachedProfileFields(cachedProfile);
  const cachedHunts = getCachedHunts();
  const [photoUrl, setPhotoUrl] = useState(cachedProfileFields.photoUrl);
  const [firstName, setFirstName] = useState(cachedProfileFields.firstName);
  const [lastName, setLastName] = useState(cachedProfileFields.lastName);
  const [handle, setHandle] = useState(cachedProfileFields.handle);
  const [preferredMethod, setPreferredMethod] = useState(cachedProfileFields.preferredMethod);
  const [statusText, setStatusText] = useState(cachedProfileFields.statusText);
  const [bio, setBio] = useState(cachedProfileFields.bio);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [locations, setLocations] = useState(() => getCachedHuntLocations() || []);
  const [primaryLocationId, setPrimaryLocationId] = useState(cachedProfileFields.primaryLocationId);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [clubs, setClubs] = useState(() => getCachedClubs() || []);
  const [buddyCount, setBuddyCount] = useState(() => getCachedBuddiesHub()?.buddies?.length || 0);
  const [lastHuntLine, setLastHuntLine] = useState(() => (
    cachedHunts ? formatLastHuntLine(pickMostRecentHunt(cachedHunts)) : null
  ));
  const [huntCount, setHuntCount] = useState(() => cachedHunts?.length || 0);

  const applyProfileRow = (data) => {
    const next = applyCachedProfileFields(data);
    setPhotoUrl(next.photoUrl);
    setFirstName(next.firstName);
    setLastName(next.lastName);
    setHandle(next.handle);
    setPreferredMethod(next.preferredMethod);
    setStatusText(next.statusText);
    setBio(next.bio);
    setPrimaryLocationId(next.primaryLocationId);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchProfile(api);
        if (!alive) return;
        applyProfileRow(res?.data || {});
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const cachedClubs = getCachedClubs();
    const hub = getCachedBuddiesHub();
    if (cachedClubs) setClubs(cachedClubs);
    if (hub?.buddies) setBuddyCount(hub.buddies.length);
    if (cachedClubs && hub?.buddies) return undefined;

    let alive = true;
    (async () => {
      try {
        const jobs = [];
        if (!cachedClubs) {
          jobs.push(
            fetchClubs(api).then((clubsRes) => {
              const clubList = clubsFromResponse(clubsRes);
              if (Array.isArray(clubList)) setCachedClubs(clubList);
              if (alive) setClubs(clubList);
            })
          );
        }
        if (!hub?.buddies) {
          jobs.push(
            buddiesService.listBuddies().then((buddies) => {
              if (alive) setBuddyCount(Array.isArray(buddies) ? buddies.length : 0);
            })
          );
        }
        await Promise.all(jobs);
      } catch (_) {
        if (!alive) return;
        if (!cachedClubs) setClubs([]);
        if (!hub?.buddies) setBuddyCount(0);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const cached = getCachedHunts();
    if (cached) {
      setHuntCount(cached.length);
      setLastHuntLine(formatLastHuntLine(pickMostRecentHunt(cached)));
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const data = await huntService.getHunts();
        const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
        setCachedHunts(list);
        if (!alive) return;
        setHuntCount(list.length);
        setLastHuntLine(formatLastHuntLine(pickMostRecentHunt(list)));
      } catch (_) {
        if (!alive) return;
        setHuntCount(0);
        setLastHuntLine(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await fetchHuntLocations(api);
        if (!alive) return;
        setLocations(Array.isArray(resp?.data) ? resp.data : []);
      } catch (e) {
        if (!alive) return;
        setLocations([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const primaryLocationLabel =
    locations.find((loc) => String(loc.id) === primaryLocationId)?.name || 'Not set';

  const weaponLabel = METHOD_LABELS[preferredMethod] || 'Not set';

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isHeic =
      /\.heic$/i.test(file.name || '') ||
      /\.heif$/i.test(file.name || '') ||
      /heic|heif/i.test(file.type || '');
    if (isHeic) {
      showToast({
        title: 'Unsupported file type (HEIC)',
        body: 'Please select a JPEG/PNG/WebP image. HEIC files are not supported.',
        variant: 'danger',
      });
      return;
    }
    setError('');
    setSuccess('');
    setUploading(true);
    try {
      const downscaleToJpeg = async (inFile) => {
        const img = await new Promise((res, rej) => {
          const im = new window.Image();
          im.onload = () => res(im);
          im.onerror = rej;
          im.src = URL.createObjectURL(inFile);
        });
        let { width, height } = img;
        const ratio = width / height;
        if (width > height) {
          if (width > 1600) {
            height = Math.round(1600 / ratio);
            width = 1600;
          }
        } else if (height > 1600) {
          width = Math.round(1600 * ratio);
          height = 1600;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        return new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
      };
      const jpegBlob = await downscaleToJpeg(file);
      const uploadContentType = 'image/jpeg';
      const uploadName = (file.name || 'upload.jpg').replace(/\.(heic|heif|png|webp)$/i, '.jpg');

      const computeSha256Hex = async (blob) => {
        const buf = await blob.arrayBuffer();
        const digest = await window.crypto.subtle.digest('SHA-256', buf);
        const arr = Array.from(new Uint8Array(digest));
        return arr.map((b) => b.toString(16).padStart(2, '0')).join('');
      };
      let shaHex = null;
      try {
        shaHex = await computeSha256Hex(file);
      } catch (_) {
        shaHex = null;
      }

      const initRes = await api.post('/assets/init/', {
        filename: uploadName,
        content_type: uploadContentType,
        scope: 'profile-photo',
        public: false,
        sha256: shaHex || undefined,
      });
      const { upload_url, object_key, reuse, asset: reused } = initRes.data || {};

      if (reuse && reused && reused.id) {
        const resp = await api.patch('/profiles/me/photo/', { asset_id: reused.id });
        patchCachedProfile(resp?.data || {});
        const url = resp?.data?.photo_url || '';
        setPhotoUrl(url ? `${url}?v=${Date.now()}` : '');
        setSuccess('Profile photo updated');
        showToast({
          title: 'Photo already exists',
          body: 'Reused your existing photo. No upload performed.',
          variant: 'info',
        });
        return;
      }

      await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': uploadContentType },
        body: jpegBlob,
      });

      let assetId = null;
      try {
        const finalized = await assetsService.finalize({
          scope: 'profile-photo',
          object_key,
          size_bytes: jpegBlob && typeof jpegBlob.size === 'number' ? jpegBlob.size : undefined,
          sha256: shaHex || undefined,
        });
        assetId = finalized?.id || null;
      } catch (_) {}

      const finalizeRes = await api.patch(
        '/profiles/me/photo/',
        assetId ? { asset_id: assetId } : { object_key }
      );
      patchCachedProfile(finalizeRes?.data || {});
      const url = finalizeRes?.data?.photo_url || '';
      setPhotoUrl(url ? `${url}?v=${Date.now()}` : '');
      setSuccess('Profile photo updated');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Monthly upload limit reached.');
        showToast({ title: 'Upload limit reached', variant: 'warning' });
      } else {
        setError('Upload failed. Please try again.');
        showToast({ title: 'Upload failed', variant: 'danger' });
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const resp = await api.patch('/profiles/me/', {
        first_name: firstName,
        last_name: lastName,
        preferred_hunting_method: preferredMethod,
        status: statusText,
        bio,
        primary_hunt_location_id: primaryLocationId ? Number(primaryLocationId) : null,
      });
      const data = resp?.data || {};
      patchCachedProfile(data);
      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
      setPreferredMethod(data.preferred_hunting_method || 'rifle');
      setStatusText(data.status || '');
      setBio(data.bio || '');
      setPrimaryLocationId(data.primary_hunt_location_id ? String(data.primary_hunt_location_id) : '');
      setSuccess('Profile updated');
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      showToast({ title: 'Profile saved', body: 'Your changes have been saved.', variant: 'success' });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save profile');
      showToast({
        title: 'Save failed',
        body: err?.response?.data?.error || 'Failed to save profile',
        variant: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    onSaveStateChange?.({ saving, savedFlash });
  }, [saving, savedFlash, onSaveStateChange]);

  useImperativeHandle(ref, () => ({
    save: handleSave,
    saving,
    savedFlash,
  }));

  const togglePreset = (preset) => {
    setStatusText((prev) => (prev === preset ? '' : preset));
  };

  const displayName = [firstName, lastName].filter(Boolean).join(' ') || 'Your Profile';
  const displayHandle = (handle || currentUser?.username || '').replace(/^@+/, '');
  const initials = getAvatarInitials(displayName === 'Your Profile' ? '' : displayName);

  return (
    <div className="y-profile-field" data-testid="profile-my-field">
      <div className="y-profile-banner">
        <div className="y-profile-topo-band">
          <div className="y-profile-topo-band__lines" aria-hidden="true" />
        </div>
        <div className="y-profile-banner__body">
          <div className="y-profile-banner__avatar-wrap">
            <div className="y-profile-banner__avatar">
              {photoUrl ? (
                <CachedImage src={photoUrl} alt="" className="y-profile-banner__avatar-img" />
              ) : (
                <div className="y-profile-banner__avatar-fallback" aria-hidden="true">
                  {initials === '?' ? <FaUser size={28} /> : initials}
                </div>
              )}
              <button
                type="button"
                className="y-profile-banner__camera-btn"
                aria-label="Upload profile photo"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <FaCamera size={12} aria-hidden="true" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </div>
            <YButton
              variant="secondary"
              size="sm"
              className="y-profile-banner__photos-btn"
              onClick={() => setShowAssetPicker(true)}
            >
              Profile Photos
            </YButton>
          </div>

          <div className="y-profile-banner__identity">
            <h2 className="y-profile-banner__name">{displayName}</h2>
            {displayHandle ? (
              <div className="y-profile-banner__handle">@{displayHandle}</div>
            ) : null}
            <div className="y-profile-banner__meta">
              <span className="y-profile-banner__meta-item">
                <FaCrosshairs aria-hidden="true" />
                {weaponLabel}
              </span>
              <span className="y-profile-banner__meta-item">
                <FaMapMarkerAlt aria-hidden="true" />
                {primaryLocationLabel}
              </span>
            </div>
          </div>

          <div className="y-profile-banner__stats">
            <ProfileStatChip value={huntCount} label="HUNTS" />
            <ProfileStatChip value={clubs.length} label="CLUBS" />
            <ProfileStatChip value={buddyCount} label="BUDDIES" />
          </div>
        </div>
      </div>

      <div className="y-profile-grid">
        <div className="y-profile-grid__main">
          <ProfilePanel icon={FaPencilAlt} title="BASIC INFORMATION">
            <div className="y-profile-fields">
              <YFormField label="First Name" htmlFor="first-name" labelClassName="y-profile-label">
                <YInput
                  id="first-name"
                  placeholder="First name"
                  value={firstName}
                  onChange={(ev) => setFirstName(ev.target.value)}
                />
              </YFormField>
              <YFormField label="Last Name" htmlFor="last-name" labelClassName="y-profile-label">
                <YInput
                  id="last-name"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(ev) => setLastName(ev.target.value)}
                />
              </YFormField>
              <div className="y-profile-fields__full">
                <p className="y-profile-label" id="pref-weapon-label">
                  Preferred Weapon
                </p>
                <div
                  className="y-profile-chip-row"
                  role="group"
                  aria-labelledby="pref-weapon-label"
                  data-testid="profile-weapon-chips"
                >
                  {WEAPON_OPTIONS.map((w) => {
                    const active = preferredMethod === w.id;
                    return (
                      <button
                        key={w.id}
                        type="button"
                        className={`y-profile-pill${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => setPreferredMethod(w.id)}
                      >
                        {w.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="y-profile-fields__full">
                <p className="y-profile-label" id="primary-location-label">
                  Primary Hunting Location
                </p>
                <div
                  className="y-profile-chip-row"
                  role="group"
                  aria-labelledby="primary-location-label"
                  data-testid="profile-location-chips"
                >
                  <button
                    type="button"
                    className={`y-profile-pill${!primaryLocationId ? ' is-active' : ''}`}
                    aria-pressed={!primaryLocationId}
                    onClick={() => setPrimaryLocationId('')}
                  >
                    None
                  </button>
                  {locations.map((loc) => {
                    const id = String(loc.id);
                    const active = primaryLocationId === id;
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        className={`y-profile-pill${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => setPrimaryLocationId(id)}
                      >
                        {loc.name || `Location ${loc.id}`}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="y-profile-fields__full">
                <YFormField label="Biography" htmlFor="bio" labelClassName="y-profile-label">
                  <YTextarea
                    id="bio"
                    placeholder="Tell other hunters about yourself, your experience, and your approach to the field…"
                    value={bio}
                    onChange={(ev) => setBio(ev.target.value.slice(0, BIO_MAX))}
                    rows={4}
                  />
                </YFormField>
                <p className="y-profile-char-count">
                  {bio.length}/{BIO_MAX}
                </p>
              </div>
            </div>
            {error ? <div className="y-profile-msg y-profile-msg--error">{error}</div> : null}
            {success ? <div className="y-profile-msg y-profile-msg--ok">{success}</div> : null}
          </ProfilePanel>

          <ProfilePanel icon={FaCompass} title="FIELD STATUS">
            <div className="y-profile-status-preview">
              <p className="y-profile-status-preview__label">CURRENT STATUS</p>
              <p className="y-profile-status-preview__text">{statusText || 'No status set'}</p>
            </div>

            <div style={{ marginTop: 16 }}>
              <p className="y-profile-label">Quick Status Tags</p>
              <div className="y-profile-chip-row">
                {STATUS_PRESETS.map((preset) => {
                  const active = statusText === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      className={`y-profile-pill y-profile-pill--soft${active ? ' is-active' : ''}`}
                      aria-pressed={active}
                      onClick={() => togglePreset(preset)}
                    >
                      {preset}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <YFormField label="Custom Status Message" htmlFor="profile-status" labelClassName="y-profile-label">
                <YTextarea
                  id="profile-status"
                  placeholder="What's happening in the field right now?"
                  value={statusText}
                  onChange={(ev) => setStatusText(ev.target.value.slice(0, STATUS_MAX))}
                  rows={2}
                />
              </YFormField>
              <div className="y-profile-status-actions">
                <span className="y-profile-char-count" style={{ marginTop: 0 }}>
                  {statusText.length}/{STATUS_MAX}
                </span>
                <YButton
                  type="button"
                  variant="primary"
                  size="sm"
                  className="y-profile-post-btn gap-2"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {savedFlash ? (
                    <FaCheck size={12} aria-hidden="true" />
                  ) : (
                    <FaSave size={12} aria-hidden="true" />
                  )}
                  <span>{savedFlash ? 'SAVED' : saving ? 'SAVING…' : 'POST STATUS'}</span>
                </YButton>
              </div>
              {error ? <div className="y-profile-msg y-profile-msg--error">{error}</div> : null}
              {success ? <div className="y-profile-msg y-profile-msg--ok">{success}</div> : null}
            </div>
          </ProfilePanel>
        </div>

        <div className="y-profile-grid__side">
          <AppearancePanel />
          <ProfilePanel icon={FaBullseye} title="HUNTER DETAILS" bodyClassName="y-profile-panel__body--flush-list">
            <div className="y-profile-loadout-row">
              <span className="y-profile-loadout-row__key">Weapon</span>
              <span className="y-profile-loadout-row__val">{weaponLabel}</span>
            </div>
            <div className="y-profile-loadout-row">
              <span className="y-profile-loadout-row__key">Go to stand</span>
              <span className="y-profile-loadout-row__val">{primaryLocationLabel}</span>
            </div>
            <div className="y-profile-loadout-row">
              <span className="y-profile-loadout-row__key">Last Hunt</span>
              <span className="y-profile-loadout-row__val">{lastHuntLine || 'No hunts logged yet'}</span>
            </div>
          </ProfilePanel>

          <ProfilePanel icon={FaAward} title="MY CLUBS">
            {clubs.length > 0 ? (
              <div className="y-profile-chip-row">
                {clubs.map((club) => (
                  <span key={club.id} className="y-profile-pill y-profile-pill--tag y-profile-pill--club">
                    {club.name || `Club ${club.id}`}
                  </span>
                ))}
              </div>
            ) : (
              <p className="y-profile-empty" style={{ border: 'none', padding: 0 }}>
                No clubs yet.
              </p>
            )}
          </ProfilePanel>

          <ProfileTip label="FIELD TIP">
            Your status shows up on your buddies&apos; feed. Set it before you walk in and
            they&apos;ll know where you are without a text.
          </ProfileTip>
        </div>
      </div>

      <AssetPickerModal
        show={showAssetPicker}
        onHide={() => setShowAssetPicker(false)}
        onSelect={async (asset) => {
          try {
            const resp = await api.patch('/profiles/me/photo/', { asset_id: asset.id });
            patchCachedProfile(resp?.data || {});
            const url = resp?.data?.photo_url || '';
            setPhotoUrl(url ? `${url}?v=${Date.now()}` : '');
            setSuccess('Profile photo updated');
            setShowAssetPicker(false);
          } catch (err) {
            console.error('Set profile from asset failed', err);
          }
        }}
      />
    </div>
  );
});
